/**
 * Lintier size selection on the product page.
 *
 * Three jobs, all layered on Horizon's own variant picker and sticky bar:
 *
 * 1. First load arrives with no size (or length) chosen. Horizon pre-selects the first
 *    available variant (US 3 on a ring), which is how wrong-size orders
 *    happen. When the URL carries no variant / option_values, the size
 *    radios are cleared and <html> is marked [data-lintier-unsized].
 * 2. While unsized, both add-to-cart buttons (in-page and sticky) read
 *    "pick a size" and open the size drawer instead of submitting.
 * 3. The size drawer lists the sizes from the live variant picker; tapping
 *    one selects the real radio, so Horizon's normal variant flow runs.
 *
 * Only radio ("buttons") pickers are handled; a dropdown picker is left as
 * Horizon renders it.
 */
(function () {
  var drawer = document.getElementById('lintier-size-drawer');
  if (!drawer) return;

  var SIZE_RE = /size|length/i;
  var ATC_SELECTOR =
    'product-form-component [ref="addToCartButton"], sticky-add-to-cart .sticky-add-to-cart__button';
  var pickSizeLabel = drawer.getAttribute('data-pick-label') || 'pick a size';
  var pickLengthLabel = drawer.getAttribute('data-pick-length-label') || 'pick a length';
  var changeLabel = drawer.getAttribute('data-change-label') || 'change size';
  var originalLabels = new WeakMap();
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- the options that must be chosen ----------------------------
     Every radio option with a real choice (two or more values) is
     required: size or length first, then anything else the product
     carries (chain design, letter, metal). Nothing is pre-selected. */

  function sizeRadios(fieldset) {
    return Array.prototype.slice.call(fieldset.querySelectorAll('input[type="radio"]'));
  }

  function optionName(fieldset) {
    var input = fieldset.querySelector('input[type="radio"]');
    return (input && input.getAttribute('data-option-name')) || '';
  }

  function requiredFieldsets() {
    /* in page order, top to bottom: the first unpicked option is the one
       the buttons and the drawer ask for next */
    return Array.prototype.slice.call(document.querySelectorAll('variant-picker fieldset')).filter(function (f) {
      return sizeRadios(f).length > 1;
    });
  }

  function sizeFieldset() {
    var list = requiredFieldsets();
    for (var i = 0; i < list.length; i++) if (SIZE_RE.test(optionName(list[i]))) return list[i];
    return list[0] || null;
  }

  function fieldsetChosen(fieldset) {
    return sizeRadios(fieldset).some(function (radio) {
      return radio.checked;
    });
  }

  /* Options the shopper has actually picked, by option name. Horizon
     re-renders the picker after every change with the whole variant
     selected, which would quietly fill in the chain (or letter) for
     them; enforce() clears anything they have not chosen themselves. */
  var chosen = {};

  function enforce() {
    requiredFieldsets().forEach(function (fieldset) {
      var name = optionName(fieldset).toLowerCase();
      if (chosen[name]) return;
      sizeRadios(fieldset).forEach(function (radio) {
        if (!radio.checked) return;
        radio.checked = false;
        radio.setAttribute('data-current-checked', 'false');
        radio.setAttribute('data-previous-checked', 'false');
      });
    });
  }

  function missingFieldsets() {
    return requiredFieldsets().filter(function (f) {
      return !fieldsetChosen(f);
    });
  }

  /* "pick a size" / "pick a length", then "pick a chain", "pick a letter" */
  function labelFor(fieldset) {
    var name = optionName(fieldset);
    if (/length/i.test(name)) return pickLengthLabel;
    if (/size/i.test(name)) return pickSizeLabel;
    if (/chain|design/i.test(name)) return 'pick a chain';
    var word = name.trim().split(/\s+/).pop().toLowerCase();
    return 'pick a ' + word;
  }

  function pickLabel() {
    var missing = missingFieldsets();
    return missing.length ? labelFor(missing[0]) : pickSizeLabel;
  }

  function sizeChosen() {
    return missingFieldsets().length === 0;
  }

  /* ---- unsized state ---------------------------------------------- */

  /* The in-page button keeps its label in a bare text node; the sticky
     bar's sits in the first span of its text content. */
  function labelNode(button) {
    var sticky = button.querySelector('.add-to-cart-text__content > span');
    if (sticky) return sticky;
    for (var i = 0; i < button.childNodes.length; i++) {
      var node = button.childNodes[i];
      if (node.nodeType === 3 && node.textContent.trim()) return node;
    }
    return null;
  }

  /* The sticky button keeps a fixed width, so its label can cross-fade
     in place: a ghost copy of the new label fades in over the old one
     for 150ms, then the real text is swapped underneath. Text nodes
     (the in-page button) just swap. */
  function swapLabel(node, text) {
    var span = node.nodeType === 1 ? node : null;
    if (!span || !span.animate || reduced.matches) {
      node.textContent = text;
      return;
    }
    var host = span.closest('.sticky-add-to-cart__button');
    if (!host) {
      span.textContent = text;
      return;
    }
    var ghost = document.createElement('span');
    ghost.textContent = text;
    ghost.setAttribute('aria-hidden', 'true');
    ghost.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;opacity:0;pointer-events:none';
    host.appendChild(ghost);
    var out = span.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150, easing: 'ease', fill: 'forwards' });
    ghost.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 150, easing: 'ease' }).finished.then(
      function () {
        span.textContent = text;
        out.cancel();
        ghost.remove();
      },
      function () {
        span.textContent = text;
        ghost.remove();
      }
    );
  }

  function setUnsized(on) {
    document.documentElement.toggleAttribute('data-lintier-unsized', on);
    var buttons = document.querySelectorAll(ATC_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      var node = labelNode(buttons[i]);
      if (!node) continue;
      if (on) {
        if (!originalLabels.has(buttons[i])) originalLabels.set(buttons[i], node.textContent);
        swapLabel(node, pickLabel());
      } else if (originalLabels.has(buttons[i])) {
        swapLabel(node, originalLabels.get(buttons[i]));
        originalLabels.delete(buttons[i]);
      }
    }
  }

  function unselectOnFirstLoad() {
    if (/[?&](variant|option_values)=/.test(location.search)) return;
    var fieldsets = requiredFieldsets();
    if (!fieldsets.length) return;
    fieldsets.forEach(function (fieldset) {
      sizeRadios(fieldset).forEach(function (radio) {
        radio.checked = false;
        radio.setAttribute('data-current-checked', 'false');
        radio.setAttribute('data-previous-checked', 'false');
      });
    });
    setUnsized(true);
  }

  /* Capture phase, so this runs before Horizon's delegated click handlers
     and before the submit button submits. */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      var button = target.closest(ATC_SELECTOR);
      if (!button || sizeChosen()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      openDrawer();
    },
    true
  );

  var VARIANT_SELECTOR = 'sticky-add-to-cart .sticky-add-to-cart__variant';

  function decorateVariant() {
    var variant = document.querySelector(VARIANT_SELECTOR);
    if (!variant || variant.getAttribute('role') === 'button') return;
    variant.setAttribute('role', 'button');
    variant.setAttribute('tabindex', '0');
    variant.setAttribute('aria-label', changeLabel);
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element) || !target.closest(VARIANT_SELECTOR)) return;
    openDrawer();
  });

  document.addEventListener('keydown', function (event) {
    var target = event.target;
    if (!(target instanceof Element) || !target.matches(VARIANT_SELECTOR)) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openDrawer();
  });

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (!target.closest('variant-picker')) return;
    chosen[(target.getAttribute('data-option-name') || '').toLowerCase()] = true;
    enforce();
    if (sizeChosen()) {
      setUnsized(false);
      if (drawer.open) closeDrawer();
    } else {
      /* one option down, the next one still to pick: relabel the buttons */
      setUnsized(true);
      if (drawer.open) buildList();
    }
  });

  /* ---- the drawer ------------------------------------------------- */

  var list = drawer.querySelector('[data-size-list]');
  var title = drawer.querySelector('[data-size-title]');
  var note = drawer.querySelector('[data-size-note]');
  var guideLink = drawer.querySelector('[data-size-guide]');

  function buildList() {
    var fieldsets = requiredFieldsets();
    list.innerHTML = '';
    if (!fieldsets.length) return;

    var sizeSet = sizeFieldset();
    /* the guide row's label ("ring size · us") already names the scale */
    var rowLabel = document.querySelector('.lintier-sg-row__label');
    var names = fieldsets.map(function (f) {
      if (f === sizeSet && rowLabel) return rowLabel.textContent.trim().toLowerCase();
      var n = optionName(f).toLowerCase();
      return /chain|design/.test(n) ? 'chain' : n;
    });
    title.textContent = names.join(' & ');
    if (note) note.hidden = !/ring/i.test(optionName(sizeSet || fieldsets[0]));
    if (guideLink) guideLink.hidden = !document.querySelector('[data-sg-open]');

    fieldsets.forEach(function (fieldset, index) {
      var group = document.createElement('div');
      group.className = 'lintier-size-drawer__group';
      if (fieldsets.length > 1) {
        var heading = document.createElement('span');
        heading.className = 'lintier-size-drawer__group-title';
        heading.textContent = names[index];
        group.appendChild(heading);
      }
      var options = document.createElement('div');
      options.className = 'lintier-size-drawer__options';
      sizeRadios(fieldset).forEach(function (radio) {
        var option = document.createElement('button');
        option.type = 'button';
        option.className = 'lintier-size-drawer__option';
        /* display only — the radio keeps its full value */
        option.textContent = /adjustable/i.test(radio.value)
          ? radio.value.replace(/\(\s*fits sizes US\s*/i, '· fits US ').replace(/\)/g, '').replace(/-/g, '\u2013')
          : radio.value.replace(/^US\s+/i, '');
        if (radio.getAttribute('data-option-available') === 'false') {
          option.disabled = true;
          option.classList.add('lintier-size-drawer__option--unavailable');
        }
        if (radio.checked) option.setAttribute('aria-pressed', 'true');
        option.addEventListener('click', function () {
          /* A dispatched change reaches Horizon even while the page behind
             the modal is inert; a synthetic click would not. The change
             handler above closes the drawer once every option is chosen,
             or rebuilds it to show the remaining one. */
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
        options.appendChild(option);
      });
      group.appendChild(options);
      list.appendChild(group);
    });
  }

  function openDrawer() {
    buildList();
    if (drawer.open) return;
    drawer.showModal();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        drawer.classList.add('lintier-size-drawer--open');
      });
    });
  }

  function closeDrawer() {
    if (!drawer.open) return;
    drawer.classList.remove('lintier-size-drawer--open');
    if (reduced.matches) {
      drawer.close();
      return;
    }
    var done = function (event) {
      if (event.target !== drawer || event.propertyName !== 'transform') return;
      drawer.removeEventListener('transitionend', done);
      drawer.close();
    };
    drawer.addEventListener('transitionend', done);
  }

  drawer.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-size-drawer-close]')) {
      closeDrawer();
      return;
    }
    /* click on the scrim: the dialog itself is the target */
    if (target === drawer) closeDrawer();
  });

  drawer.addEventListener('cancel', function (event) {
    event.preventDefault();
    closeDrawer();
  });

  if (guideLink) {
    guideLink.addEventListener('click', function (event) {
      event.preventDefault();
      closeDrawer();
      var opener = document.querySelector('[data-sg-open]');
      if (opener) {
        setTimeout(function () {
          opener.click();
        }, reduced.matches ? 0 : 320);
      }
    });
  }

  /* ---- init: after Horizon's variant-picker has read its initial state */

  var initialised = false;
  function init() {
    if (initialised) return;
    initialised = true;
    unselectOnFirstLoad();
    decorateVariant();
    var picker = document.querySelector('variant-picker');
    var host = picker && (picker.closest('product-form-component, .product-details, form') || picker.parentElement);
    if (host && window.MutationObserver) {
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          enforce();
          setUnsized(!sizeChosen());
          if (drawer.open) buildList();
        });
      }).observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'data-current-checked'] });
    }
    var sticky = document.querySelector('sticky-add-to-cart');
    if (sticky && window.MutationObserver) {
      new MutationObserver(decorateVariant).observe(sticky, { childList: true, subtree: true, attributes: true });
    }
  }

  function ready() {
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('variant-picker').then(init, init);
      setTimeout(init, 1500);
    } else {
      init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }
})();
